import React, { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../services/api";

// ── Types ─────────────────────────────────────────────────────────────────────
interface TelemetrySummary {
  total_events: number;
  last_app_start: string;
  app_starts: number;
  llm: {
    calls: number;
    failures: number;
    avg_latency_s: number;
    max_latency_s: number;
    total_tokens: number;
  };
  uploads: {
    manual: number;
    batch_started: number;
    batch_completed: number;
  };
  push: { success: number; failed: number };
  errors: { api_errors: number; exceptions: number };
  recent_events: RecentEvent[];
}

interface RecentEvent {
  event: string;
  timestamp: string;
  latency: number | null;
  tokens: number | null;
  label: string | null;
  status: string | null;
  file_ext: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const EVENT_ICON: Record<string, string> = {
  app_started:            "🚀",
  llm_call_completed:     "🤖",
  llm_call_failed:        "❌",
  manual_upload_completed:"📄",
  batch_scan_started:     "🗂",
  batch_scan_completed:   "✅",
  push_completed:         "📤",
  skip_completed:         "⏭",
  api_error:              "⚠️",
  exception:              "🔴",
};

const EVENT_COLOR: Record<string, string> = {
  llm_call_failed: "#fdecea",
  api_error:       "#fdecea",
  exception:       "#fdecea",
  push_completed:  "#e8f5e9",
  llm_call_completed: "#e3f2fd",
  manual_upload_completed: "#f3e5f5",
  batch_scan_completed: "#e8f5e9",
};

function fmtTime(iso: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
}

function fmtDate(iso: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard: React.FC<{ label: string; value: string | number; sub?: string; accent?: string }> =
  ({ label, value, sub, accent = "#1565c0" }) => (
    <div style={{ ...styles.card, borderTop: `4px solid ${accent}` }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#333", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{sub}</div>}
    </div>
  );

// ── Main Page ────────────────────────────────────────────────────────────────
const TelemetryPage: React.FC = () => {
  const [data, setData]         = useState<TelemetrySummary | null>(null);
  const [error, setError]       = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [loading, setLoading]   = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearMsg, setClearMsg] = useState<string | null>(null);

  const handleClear = async () => {
    if (!window.confirm("Clear all telemetry logs? This cannot be undone.")) return;
    setClearing(true);
    setClearMsg(null);
    try {
      const res = await fetch(`${API_BASE_URL}/telemetry/clear`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      setClearMsg("Logs cleared.");
      await fetch_summary();
    } catch (e: any) {
      setClearMsg(`Failed: ${e.message}`);
    } finally {
      setClearing(false);
      setTimeout(() => setClearMsg(null), 3000);
    }
  };

  const fetch_summary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/telemetry/summary`);
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e.message || "Failed to load telemetry.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh every 15 seconds — ref prevents stale closure + interval stacking
  const loadingRef = React.useRef(false);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => {
    fetch_summary();
    const id = setInterval(() => {
      if (!loadingRef.current) fetch_summary();
    }, 15000);
    return () => clearInterval(id);
  }, [fetch_summary]);

  if (error) return (
    <div style={styles.container}>
      <div style={styles.errorBox}>⚠️ {error}</div>
    </div>
  );

  if (!data) return (
    <div style={styles.container}>
      <div style={{ color: "#888", marginTop: 40, textAlign: "center" }}>Loading telemetry…</div>
    </div>
  );

  const totalErrors = data.errors.api_errors + data.errors.exceptions + data.llm.failures;

  return (
    <div style={styles.container}>

      {/* ── Header ── */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>📊 Telemetry Dashboard</h2>
          <span style={styles.sub}>Last refresh: {lastRefresh.toLocaleTimeString()} · Auto-refreshes every 15s</span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {clearMsg && (
            <span style={{ fontSize: 12, color: clearMsg.startsWith("Failed") ? "#c62828" : "#2e7d32", fontWeight: 600 }}>
              {clearMsg}
            </span>
          )}
          <button style={styles.clearBtn} onClick={handleClear} disabled={clearing || loading}>
            {clearing ? "Clearing…" : "🗑 Clear Logs"}
          </button>
          <button style={styles.refreshBtn} onClick={fetch_summary} disabled={loading}>
            {loading ? "Refreshing…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div style={styles.cardRow}>
        <StatCard label="LLM Calls"        value={data.llm.calls}            accent="#1565c0" sub={`${data.llm.failures} failed`} />
        <StatCard label="Avg LLM Latency"  value={`${data.llm.avg_latency_s}s`} accent="#6a1b9a" sub={`Max ${data.llm.max_latency_s}s`} />
        <StatCard label="Total Tokens"     value={data.llm.total_tokens.toLocaleString()} accent="#00695c" sub="across all LLM calls" />
        <StatCard label="Manual Uploads"   value={data.uploads.manual}       accent="#e65100" />
        <StatCard label="Batch Scans"      value={data.uploads.batch_completed} accent="#1565c0" sub={`${data.uploads.batch_started} started`} />
        <StatCard label="Pushed to Alation" value={data.push.success}        accent="#2e7d32" sub={data.push.failed ? `${data.push.failed} failed` : undefined} />
        <StatCard label="Errors"           value={totalErrors}               accent={totalErrors > 0 ? "#c62828" : "#2e7d32"} sub={`${data.errors.api_errors} API · ${data.errors.exceptions} exceptions`} />
        <StatCard label="App Starts"       value={data.app_starts}           accent="#546e7a" sub={`Last: ${fmtDate(data.last_app_start)}`} />
      </div>

      {/* ── Recent Events Table ── */}
      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>Recent Events</div>
        <table style={styles.table}>
          <thead>
            <tr style={styles.thead}>
              <th style={styles.th}>Time</th>
              <th style={styles.th}>Event</th>
              <th style={styles.th}>Latency</th>
              <th style={styles.th}>Tokens</th>
              <th style={styles.th}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_events.map((ev, i) => (
              <tr key={i} style={{ background: EVENT_COLOR[ev.event] || "#fff", borderBottom: "1px solid #f0f0f0" }}>
                <td style={styles.td}>{fmtTime(ev.timestamp)}</td>
                <td style={styles.td}>
                  <span style={{ marginRight: 6 }}>{EVENT_ICON[ev.event] || "•"}</span>
                  <span style={{ fontSize: 12, fontFamily: "monospace" }}>{ev.event}</span>
                </td>
                <td style={{ ...styles.td, textAlign: "right" }}>
                  {ev.latency != null ? `${ev.latency.toFixed(2)}s` : "—"}
                </td>
                <td style={{ ...styles.td, textAlign: "right" }}>
                  {ev.tokens != null ? ev.tokens.toLocaleString() : "—"}
                </td>
                <td style={{ ...styles.td, color: "#555", fontSize: 12 }}>
                  {ev.status ? <span style={{ fontWeight: 600, color: ev.status === "success" ? "#2e7d32" : "#c62828" }}>{ev.status}</span>
                    : ev.file_ext ? <span>ext: {ev.file_ext}</span>
                    : ev.label ? <span>{ev.label}</span>
                    : "—"}
                </td>
              </tr>
            ))}
            {data.recent_events.length === 0 && (
              <tr><td colSpan={5} style={{ ...styles.td, textAlign: "center", color: "#aaa" }}>No events yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  container:    { maxWidth: 1200, margin: "0 auto", padding: "24px 16px" },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 },
  title:        { margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a2e" },
  sub:          { fontSize: 12, color: "#888" },
  refreshBtn:   { padding: "8px 18px", background: "#1565c0", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 },
  clearBtn:     { padding: "8px 18px", background: "#fff", color: "#c62828", border: "1px solid #c62828", borderRadius: 6, cursor: "pointer", fontSize: 13 },
  cardRow:      { display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 28 },
  card:         { background: "#fff", borderRadius: 10, padding: "16px 20px", minWidth: 130, flex: "1 1 130px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  tableCard:    { background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" },
  tableHeader:  { padding: "14px 20px", fontWeight: 700, fontSize: 14, color: "#333", borderBottom: "1px solid #f0f0f0", background: "#fafafa" },
  table:        { width: "100%", borderCollapse: "collapse" },
  thead:        { background: "#f5f5f5" },
  th:           { padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#555", borderBottom: "2px solid #e8e8e8" },
  td:           { padding: "9px 14px", fontSize: 13, verticalAlign: "middle" },
  errorBox:     { background: "#fdecea", border: "1px solid #f5c6c6", borderRadius: 8, padding: "16px 20px", color: "#c62828" },
};

export default TelemetryPage;
